// AI-CONTEXT: See docs/features/compare-two-periods.md
//
// Resolves a card's period+compare control value into the two date ranges the
// ComparisonFrame renders. Mirrors resolveWidgetPeriod's "empty range → null"
// semantics so downstream (periodRangeToDates → useHierarchyData) treats an
// unbounded range as all-time.
//
// A null rangeA/rangeB means "unbounded" (all data). `compareActive` is the
// separate signal for "show two series at all" — it follows the explicit
// `compare.enabled` flag, so a card can compare a bounded period against an
// unbounded (all-time) baseline.

/**
 * @typedef {object} PeriodRange
 * @property {string|null} start - ISO date 'YYYY-MM-DD' or null (unbounded)
 * @property {string|null} end - ISO date 'YYYY-MM-DD' or null (unbounded)
 */

/**
 * @typedef {object} CardPeriodValue
 * @property {PeriodRange} [period] - Primary period
 * @property {{enabled?: boolean, start?: string|null, end?: string|null}} [compare] - Compare period
 */

/** @param {{start?: string|null, end?: string|null}|null|undefined} range @returns {PeriodRange|null} */
const asRangeOrNull = (range) => {
    const start = range?.start || null
    const end = range?.end || null
    return (start || end) ? { start, end } : null
}

/**
 * @param {CardPeriodValue|null|undefined} value - The card's period+compare setting
 * @returns {{rangeA: PeriodRange|null, rangeB: PeriodRange|null, compareActive: boolean}}
 */
const resolveComparePeriods = (value) => {
    const compareActive = value?.compare?.enabled === true
    return {
        rangeA: asRangeOrNull(value?.period),
        rangeB: compareActive ? asRangeOrNull(value?.compare) : null,
        compareActive,
    }
}

export default resolveComparePeriods
